import ExcelJS from 'exceljs';

/**
 * excelGenerator.js  —  VATICA V5
 * ─────────────────────────────────────────────────────────────────────────────
 * Mirrors retail_extractor.py's three sheet builders EXACTLY so the
 * browser-generated Excel matches the Python-generated test_invoice Excel.
 *
 * Sheet 1 "VAT Summary"       — 14 cols, one row per invoice
 * Sheet 2 "Review Issues"     — 4 cols, source filename + confidence + issues
 * Sheet 3 "Line Items Detail" — 10 cols, one row per line item
 *
 * generateVatExcel(purchaseResults, reviewResults, clientName, monthLabel)
 *   purchaseResults : array of vat_engine analyse_vat_inline() output dicts
 *                     each MUST carry _source_file and line_items
 *   reviewResults   : array of { confidence, issues[] }
 */

// ── Colour constants (match retail_extractor.py) ──────────────────────────────
const C_DARK  = '1F3864';   // dark navy
const C_HDR   = '2E5FA3';   // mid-blue  — column headers
const C_TOTAL = '1A3A5C';   // total row
const C_WHITE = 'FFFFFF';
const C_GREEN = 'E2EFDA';   // HIGH confidence row bg
const C_AMBER = 'FFF2CC';   // MEDIUM
const C_RED   = 'FCE4D6';   // LOW
const MONEY   = '"E "#,##0.00';

// ── Style helpers ─────────────────────────────────────────────────────────────
const hfont  = (sz = 10, color = C_WHITE) =>
  ({ bold: true, size: sz, color: { argb: color }, name: 'Calibri' });

const dfont  = (sz = 10, color = '000000', bold = false) =>
  ({ size: sz, color: { argb: color }, name: 'Calibri', bold });

const fill   = (hex) =>
  ({ type: 'pattern', pattern: 'solid', fgColor: { argb: hex } });

const bdr    = (style = 'thin', color = 'CCCCCC') => ({
  left:   { style, color: { argb: color } },
  right:  { style, color: { argb: color } },
  top:    { style, color: { argb: color } },
  bottom: { style, color: { argb: color } },
});

const mbdr   = () => bdr('medium', C_DARK);

const ALN_C  = { horizontal: 'center', vertical: 'center', wrapText: true };
const ALN_L  = { horizontal: 'left',   vertical: 'center', wrapText: true };
const ALN_R  = { horizontal: 'right',  vertical: 'center' };

function confBg(conf) {
  if (conf === 'HIGH')   return fill(C_GREEN);
  if (conf === 'MEDIUM') return fill(C_AMBER);
  return fill(C_RED);
}

function confColor(conf) {
  if (conf === 'HIGH')   return '1F3864';
  if (conf === 'MEDIUM') return 'E65100';
  return 'B71C1C';
}

// ── set cell shorthand ────────────────────────────────────────────────────────
function sc(ws, row, col, val, font, fillStyle, align, fmt, border) {
  const cell = ws.getCell(row, col);
  cell.value = val ?? null;
  if (font)      cell.font      = font;
  if (fillStyle) cell.fill      = fillStyle;
  if (align)     cell.alignment = align;
  if (fmt)       cell.numFmt    = fmt;
  if (border)    cell.border    = border;
  return cell;
}

// ── Invoice colour palette for Line Items separator rows ─────────────────────
const INV_COLORS = ['1F3864','1A5276','154360','1B4F72','21618C','117A65','6E2F0A'];


// ══════════════════════════════════════════════════════════════════════════════
//  PUBLIC API
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Build and download the VAT Purchases Excel.
 * Returns the raw ArrayBuffer so the caller can upload it to Report History.
 *
 * @param {Array}  purchaseResults  - vat_engine output dicts (with _source_file, line_items)
 * @param {Array}  reviewResults    - { confidence, issues[] } per invoice
 * @param {string} clientName
 * @param {string} monthLabel       - e.g. "May 2026"
 */
export async function generateVatExcel(
  purchaseResults = [],
  reviewResults   = [],
  clientName      = '',
  monthLabel      = '',
  skipDownload    = false,
) {
  const wb = new ExcelJS.Workbook();

  _buildVatSummarySheet(wb.addWorksheet('VAT Summary'),       purchaseResults, reviewResults, clientName, monthLabel);
  _buildReviewSheet    (wb.addWorksheet('Review Issues'),      purchaseResults, reviewResults);
  _buildLineItemsSheet (wb.addWorksheet('Line Items Detail'),  purchaseResults);

  const buffer = await wb.xlsx.writeBuffer();

  // Trigger browser download (skipped when called from merge workflow)
  if (!skipDownload) {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href  = url;
    const safeClient = clientName ? `_${clientName.replace(/\s+/g, '_')}` : '';
    const today      = new Date().toISOString().split('T')[0];
    link.download    = `VAT_Purchases_Report${safeClient}_${today}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return buffer;
}


// ══════════════════════════════════════════════════════════════════════════════
//  SHEET 1 — VAT SUMMARY  (mirrors build_vat_summary_sheet in retail_extractor.py)
//  15 columns:
//  # | Invoice Date | Supplier VAT No | Supplier Name | Supplier Address |
//  Customer VAT No | Invoice No | Subtotal (E) | Standard Rated (E) |
//  Zero-Rated (E) | Exempted (E) | VAT Amount (E) | Total (E) | Confidence | Source File
// ══════════════════════════════════════════════════════════════════════════════

function _buildVatSummarySheet(ws, invoices, reviewResults, clientName, monthLabel) {
  const N_COLS     = 15;
  const DATA_START = 4;
  const dataEnd    = DATA_START + invoices.length - 1;
  const totalRow   = dataEnd + 1;
  const tipRow     = totalRow + 1;

  // Column widths
  const widths = [5, 14, 18, 28, 25, 18, 16, 18, 13, 13, 13, 16, 13, 12, 32];
  widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

  // Row 1 — Title
  ws.getRow(1).height = 40;
  ws.mergeCells(`A1:${_col(N_COLS)}1`);
  const lbl = monthLabel ? ` | ${monthLabel}` : '';
  sc(ws, 1, 1,
    `VAT AUTOMATION SYSTEM — PURCHASES SUMMARY | ${(clientName || '').toUpperCase()}${lbl}`,
    hfont(15), fill(C_DARK), ALN_C);

  // Row 2 — Subtitle
  ws.getRow(2).height = 16;
  ws.mergeCells(`A2:${_col(N_COLS)}2`);
  sc(ws, 2, 1,
    'Amounts in Emalangeni (E)   |   Editable — correct any value if needed   |   ' +
    'Confidence: GREEN = High   ORANGE = Low / Needs Review',
    dfont(9, '1F3864'), null, ALN_C);

  // Row 3 — Headers
  ws.getRow(3).height = 28;
  ws.views = [{ state: 'frozen', ySplit: 3 }];
  const hdrs = [
    '#', 'Invoice Date', 'Supplier VAT No',
    'Supplier Name', 'Supplier Address',
    'Customer VAT No', 'Invoice No', 'Subtotal (E)',
    'Standard Rated (E)', 'Zero-Rated (E)', 'Exempted (E)',
    'VAT Amount (E)', 'Total (E)', 'Confidence', 'Source File',
  ];
  hdrs.forEach((h, i) => {
    sc(ws, 3, i + 1, h, hfont(10), fill(C_HDR), ALN_C, null, bdr());
  });

  // Data rows
  invoices.forEach((inv, i) => {
    const rv   = reviewResults[i] || {};
    const conf = rv.confidence || 'HIGH';
    const row  = DATA_START + i;
    const bg   = confBg(conf);

    ws.getRow(row).height = 20;

    const subtotal  = parseFloat(inv.subtotal   || 0);
    const vat       = parseFloat(inv.vat_amount || 0);
    const total     = parseFloat(inv.total      || 0);
    const stdRated  = parseFloat(inv.standard_rated_amount || 0);
    const zeroRated = parseFloat(inv.zero_rated_amount || inv.zero_exempt_amount || 0);
    const exempted  = parseFloat(inv.exempt_amount || 0);

    // Parse invoice date → Date object so Excel can apply DD-MM-YYYY format
    const rawDate = inv.invoice_date || '';
    const parsedDate = rawDate ? (() => {
      const [y, m, d] = rawDate.split('-').map(Number);
      return (!isNaN(y) && !isNaN(m) && !isNaN(d)) ? new Date(y, m - 1, d) : rawDate;
    })() : '';

    const vals = [
      i + 1,                             // col 1:  #
      parsedDate,                        // col 2:  Invoice Date
      inv.supplier_vat_number || '',     // col 3:  Supplier VAT No
      inv.supplier_name       || '',     // col 4:  Supplier Name
      inv.supplier_address    || '',     // col 5:  Supplier Address
      inv.customer_vat_number || '',     // col 6:  Customer VAT No
      inv.invoice_number      || '',     // col 7:  Invoice No
      subtotal,                          // col 8:  Subtotal (E)
      stdRated,                          // col 9:  Standard Rated (E)
      zeroRated,                         // col 10: Zero-Rated (E)
      exempted,                          // col 11: Exempted (E)
      vat,                               // col 12: VAT Amount (E)
      total,                             // col 13: Total (E)
      conf,                              // col 14: Confidence
      inv._source_file || '',            // col 15: Source File
    ];

    vals.forEach((val, ci) => {
      const col  = ci + 1;
      const isMoney = [8,9,10,11,12,13].includes(col);
      const isConf  = col === 14;
      const isNum   = col === 1;
      const isDate  = col === 2;

      sc(ws, row, col, val,
        isConf
          ? dfont(10, confColor(conf), true)
          : dfont(10),
        bg,
        isConf ? ALN_C : isNum ? ALN_C : isDate ? ALN_C : isMoney ? ALN_R : ALN_L,
        isMoney ? MONEY : isDate ? 'DD-MM-YYYY' : null,
        bdr(),
      );
    });
  });

  // Grand total row
  ws.getRow(totalRow).height = 28;
  ws.mergeCells(`A${totalRow}:G${totalRow}`);
  sc(ws, totalRow, 1, 'GRAND TOTAL', hfont(12), fill(C_TOTAL), ALN_C, null, mbdr());

  for (let col = 8; col <= 13; col++) {
    const ltr = _col(col);
    const cell = ws.getCell(totalRow, col);
    cell.value         = { formula: `SUM(${ltr}${DATA_START}:${ltr}${dataEnd})` };
    cell.font          = hfont(12);
    cell.fill          = fill(C_TOTAL);
    cell.numFmt        = MONEY;
    cell.alignment     = ALN_R;
    cell.border        = mbdr();
  }
  ws.getCell(totalRow, 14).fill   = fill(C_TOTAL);
  ws.getCell(totalRow, 14).border = mbdr();
  ws.getCell(totalRow, 15).fill   = fill(C_TOTAL);
  ws.getCell(totalRow, 15).border = mbdr();

  // Tip row
  ws.getRow(tipRow).height = 18;
  ws.mergeCells(`A${tipRow}:${_col(N_COLS)}${tipRow}`);
  sc(ws, tipRow, 1,
    '   TIP: GREEN = high confidence (maths correct, all fields found)   |   ' +
    'ORANGE = low confidence or validation issue — please spot-check the original invoice.',
    dfont(9, '1F3864'), fill('F0F4FF'), ALN_L);

  // Auto filter
  ws.autoFilter = { from: 'A3', to: `${_col(N_COLS)}${dataEnd}` };
}


// ══════════════════════════════════════════════════════════════════════════════
//  SHEET 2 — REVIEW ISSUES  (mirrors build_review_sheet in retail_extractor.py)
//  4 columns: # | Invoice File | Confidence | Issues Found
// ══════════════════════════════════════════════════════════════════════════════

function _buildReviewSheet(ws, invoices, reviewResults) {
  // Column widths
  [4, 40, 12, 80].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

  // Row 1 — Title
  ws.getRow(1).height = 35;
  ws.mergeCells('A1:D1');
  sc(ws, 1, 1, 'EXTRACTION REVIEW — ISSUES & CONFIDENCE REPORT',
    hfont(13), fill(C_DARK), ALN_C);

  // Row 2 — Headers
  ws.getRow(2).height = 24;
  ['#', 'Invoice File', 'Confidence', 'Issues Found'].forEach((h, i) => {
    sc(ws, 2, i + 1, h, hfont(10), fill(C_HDR), ALN_C, null, bdr());
  });

  // Data rows
  invoices.forEach((inv, i) => {
    const rv       = reviewResults[i] || {};
    const conf     = rv.confidence || 'HIGH';
    const issues   = rv.issues || [];
    const issueStr = issues.length > 0 ? issues.join('; ') : 'No issues found';
    // Source file: tagged by api_server.py onto both inv and rv
    const srcFile  = inv._source_file || rv.filename || rv.source_file || '';
    const row      = i + 3;
    const bg       = confBg(conf);

    ws.getRow(row).height = 18;

    sc(ws, row, 1, i + 1,    dfont(10), bg, ALN_C, null, bdr());
    sc(ws, row, 2, srcFile,  dfont(10), bg, ALN_L, null, bdr());
    sc(ws, row, 3, conf,     dfont(10, confColor(conf), true), bg, ALN_C, null, bdr());
    sc(ws, row, 4, issueStr, dfont(10), bg, ALN_L, null, bdr());
  });
}


// ══════════════════════════════════════════════════════════════════════════════
//  SHEET 3 — LINE ITEMS DETAIL  (mirrors build_line_items_sheet in retail_extractor.py)
//  10 columns:
//  # | Stock Code | Qty | Description | Pack |
//  Unit Price Incl. VAT (E) | Disc. Price Excl. VAT (E) |
//  Disc. Price Incl. VAT (E) | VAT % | Line Total (E)
// ══════════════════════════════════════════════════════════════════════════════

function _buildLineItemsSheet(ws, invoices) {
  const N_COLS = 10;

  // Column widths
  [4, 13, 6, 38, 6, 16, 18, 13, 8, 14].forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });

  // Row 1 — Title
  ws.getRow(1).height = 40;
  ws.mergeCells(`A1:${_col(N_COLS)}1`);
  sc(ws, 1, 1, 'VAT AUTOMATION SYSTEM — LINE ITEMS DETAIL',
    hfont(14), fill(C_DARK), ALN_C);

  // Row 2 — Subtitle
  ws.mergeCells(`A2:${_col(N_COLS)}2`);
  sc(ws, 2, 1,
    'Items and Prices extracted directly from invoices. ' +
    'Disc. Price columns are empty where not printed on the invoice.',
    { size: 9, color: { argb: '8B0000' }, name: 'Calibri', italic: true },
    null, ALN_L);

  // Row 3 — Headers
  ws.getRow(3).height = 28;
  ws.views = [{ state: 'frozen', ySplit: 3 }];
  const hdrs = [
    '#', 'Stock Code', 'Qty', 'Description', 'Pack',
    'Unit Price Incl. VAT (E)', 'Disc. Price Excl. VAT (E)',
    'Disc. Price Incl. VAT (E)', 'VAT %', 'Line Total (E)',
  ];
  hdrs.forEach((h, i) => {
    sc(ws, 3, i + 1, h, hfont(10), fill(C_HDR), ALN_C, null, bdr());
  });

  let row = 4;

  invoices.forEach((inv, invIdx) => {
    const items = inv.line_items || [];
    if (!items.length) return;

    // Supplier separator row (dark navy, merged, no border)
    ws.getRow(row).height = 18;
    ws.mergeCells(`A${row}:${_col(N_COLS)}${row}`);
    const sepText =
      `   ${inv.supplier_name || '?'}   |   ` +
      `VAT No: ${inv.supplier_vat_number || '?'}   |   ` +
      `Invoice: ${inv.invoice_number || '?'}   |   ` +
      `Date: ${inv.invoice_date || '?'}   |   ` +
      `${items.length} item(s)`;
    sc(ws, row, 1, sepText,
      { size: 9, bold: true, color: { argb: C_WHITE }, name: 'Calibri' },
      fill(INV_COLORS[invIdx % INV_COLORS.length]),
      ALN_L);
    row++;

    // Line item rows
    items.forEach((item) => {
      ws.getRow(row).height = 18;

      // VAT rate — show as "15%" string if it's a number
      const vatPct = typeof item.vat_rate === 'number'
        ? `${(item.vat_rate * 100).toFixed(0)}%`
        : (item.vat_rate || '15%');

      const vals = [
        '',                            // A — blank (matches Python output)
        item.stock_code   || '',        // B — Stock Code
        item.qty          ?? null,      // C — Qty
        item.description  || '',        // D — Description
        item.pack         ?? null,      // E — Pack
        item.unit_price   ?? null,      // F — Unit Price Incl. VAT
        item.disc_price_excl ?? null,   // G — Disc. Price Excl. VAT
        item.disc_price_incl ?? null,   // H — Disc. Price Incl. VAT
        vatPct,                        // I — VAT %
        item.line_total   ?? null,      // J — Line Total
      ];

      vals.forEach((val, ci) => {
        const col     = ci + 1;
        const isMoney = [6, 7, 8, 10].includes(col);
        sc(ws, row, col, val,
          dfont(9),
          fill('FFFFFF'),
          isMoney ? ALN_R : col === 4 ? ALN_L : ALN_C,
          isMoney ? MONEY : null,
          bdr(),
        );
      });
      row++;
    });
  });
}


// ══════════════════════════════════════════════════════════════════════════════
//  Sales Excel — unchanged
// ══════════════════════════════════════════════════════════════════════════════

/**
 * generateSalesExcel
 * ──────────────────
 * Builds a 3-sheet Sales workbook matching sales_extractor.py's export_to_excel().
 *
 * Sheet 1 — Sales Summary   : one row per invoice
 * Sheet 2 — Line Items       : one row per extracted line item
 * Sheet 3 — Review           : low-confidence / handwriting-ambiguous rows only
 *
 * Accepts two record shapes:
 *   AI-extracted  : { vendor_name, vendor_vat_tin, customer_name, customer_vat_tin,
 *                     invoice_number, date, total_incl_vat, vat_amount, category,
 *                     confidence, handwriting_detected, handwriting_ambiguous,
 *                     extraction_notes, line_items[], file }
 *   Manual entry  : { cust, vatTin, invNo, date, desc, cat, total, vat, excl, ... }
 */
export async function generateSalesExcel(salesRecords = [], clientName = '', monthLabel = '', skipDownload = false) {
  const wb = new ExcelJS.Workbook();

  const DARK     = '1B3A2D';
  const MID      = '2D6A4F';
  const WHITE    = 'FFFFFF';
  const MED_BG   = 'FEF3DC';
  const MED_FG   = '7A5200';
  const LOW_BG   = 'FFE0E0';
  const LOW_FG   = 'B71C1C';
  const MFMT     = '"E "#,##0.00';

  const _side = () => {
    const s = { style: 'thin', color: { argb: 'CCCCCC' } };
    return { left: s, right: s, top: s, bottom: s };
  };
  const _hdr = (ws, row, col, val, w) => {
    const c = ws.getCell(row, col);
    c.value     = val;
    c.font      = { bold: true, size: 10, color: { argb: WHITE }, name: 'Calibri' };
    c.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: MID } };
    c.alignment = { horizontal: 'center', vertical: 'center', wrapText: true };
    c.border    = _side();
    if (w) ws.getColumn(col).width = w;
    return c;
  };
  const _title = (ws, ncols, title) => {
    ws.mergeCells(`A1:${_col(ncols)}1`);
    ws.getRow(1).height = 36;
    const c = ws.getCell('A1');
    const lbl = monthLabel ? ` | ${monthLabel}` : '';
    c.value     = `${title}${clientName ? ' | ' + clientName.toUpperCase() : ''}${lbl}`;
    c.font      = { bold: true, size: 13, color: { argb: WHITE }, name: 'Calibri' };
    c.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } };
    c.alignment = { horizontal: 'center', vertical: 'center' };
  };

  // Normalise to a unified shape regardless of source
  const norm = (s) => {
    // Use ?? fallbacks so EITHER field name shape works correctly.
    // AI-extracted records may have vendor_name set but still use the old
    // manual field names (total, invNo, cat, vat) when pushed from ClientDashboard.
    const tot  = parseFloat(s.total_incl_vat  ?? s.total   ?? 0);
    const vat  = parseFloat(s.vat_amount       ?? s.vat     ?? 0);
    const cat  = s.category || s.cat || 'Standard';
    const conf = ((s.confidence || 'high')).toLowerCase();
    return {
      date             : s.date            || '',
      invoice_number   : s.invoice_number  || s.invNo       || '',
      vendor_name      : s.vendor_name     || '',
      vendor_vat_tin   : s.vendor_vat_tin  || '',
      customer_name    : s.customer_name   || s.cust        || '',
      customer_vat_tin : s.customer_vat_tin|| s.vatTin      || '',
      description      : s.description    || s.desc        || '',
      category         : cat,
      total_incl_vat   : tot,
      vat_amount       : vat,
      confidence       : conf,
      handwriting      : !!(s.handwriting_detected || s.handwriting),
      ambiguous        : !!(s.handwriting_ambiguous || s.ambiguous),
      notes            : s.extraction_notes || s.notes || '',
      file             : s.file            || '',
      line_items       : s.line_items      || [],
    };
  };

  const rows = salesRecords.map(norm);

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 1 — Sales Summary
  // ══════════════════════════════════════════════════════════════════════
  const ws1 = wb.addWorksheet('Sales Summary');
  _title(ws1, 13, 'VATICA AI — SALES SUMMARY');

  const S_HDR = ['Date','Invoice No','Vendor','Vendor TIN/VAT','Customer',
                 'Customer TIN/VAT','Subtotal Excl','Std Rated (Excl)',
                 'Zero Rated (Excl)','VAT Amount','Total Incl VAT',
                 'Confidence','Source File'];
  const S_W   = [13,13,22,18,22,18,16,18,18,14,16,11,30];
  ws1.getRow(2).height = 28;
  S_HDR.forEach((h, i) => _hdr(ws1, 2, i + 1, h, S_W[i]));

  let tIncl=0, tExcl=0, tStd=0, tZero=0, tVat=0;

  rows.forEach((d, i) => {
    const row = i + 3;
    ws1.getRow(row).height = 18;
    const vat  = d.vat_amount;
    const tot  = d.total_incl_vat;
    const excl = Math.round((tot - vat) * 100) / 100;
    const std  = vat > 0 ? Math.round((vat / 0.15) * 100) / 100 : (d.category === 'Standard' ? excl : 0);
    const zero = Math.max(0, Math.round((excl - std) * 100) / 100);
    const conf = d.confidence;
    const bgHex = conf === 'high' ? null : conf === 'medium' ? MED_BG : LOW_BG;
    const fgHex = conf === 'high' ? '000000' : conf === 'medium' ? MED_FG : LOW_FG;
    const bg = bgHex ? { type: 'pattern', pattern: 'solid', fgColor: { argb: bgHex } } : null;

    const vals = [d.date, d.invoice_number, d.vendor_name, d.vendor_vat_tin,
                  d.customer_name, d.customer_vat_tin,
                  excl, std, zero, vat, tot,
                  conf.toUpperCase(), d.file];

    vals.forEach((val, ci) => {
      const col  = ci + 1;
      const cell = ws1.getCell(row, col);
      cell.value  = val ?? null;
      cell.font   = { size: 10, name: 'Calibri', color: { argb: fgHex },
                      bold: col === 12 };
      if (bg) cell.fill = bg;
      cell.border = _side();
      const isM = [7,8,9,10,11].includes(col);
      if (isM) cell.numFmt = MFMT;
      cell.alignment = { horizontal: isM ? 'right' : col===1||col===12?'center':'left',
                         vertical: 'center' };
    });

    tIncl += tot; tExcl += excl; tStd += std; tZero += zero; tVat += vat;
  });

  // Total row
  const trow1 = rows.length + 3;
  ws1.getRow(trow1).height = 26;
  ws1.mergeCells(`A${trow1}:F${trow1}`);
  const tc = ws1.getCell(`A${trow1}`);
  tc.value = 'GRAND TOTAL';
  tc.font  = { bold: true, size: 12, color: { argb: WHITE }, name: 'Calibri' };
  tc.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } };
  tc.alignment = { horizontal: 'center', vertical: 'center' };
  [[7,tExcl],[8,tStd],[9,tZero],[10,tVat],[11,tIncl]].forEach(([col, val]) => {
    const cell = ws1.getCell(trow1, col);
    cell.value  = Math.round((val + Number.EPSILON) * 100) / 100;
    cell.font   = { bold: true, size: 12, color: { argb: WHITE }, name: 'Calibri' };
    cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } };
    cell.numFmt = MFMT;
    cell.alignment = { horizontal: 'right', vertical: 'center' };
  });
  for (let c = 12; c <= 13; c++) {
    const cell = ws1.getCell(trow1, c);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } };
  }

  ws1.autoFilter = { from: 'A2', to: `${_col(13)}${rows.length + 2}` };

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 2 — Line Items Detail
  // 9 columns:
  // Stock Code | Qty | Description | Pack |
  // Unit Price Incl. VAT | Disc. Price Excl. | Disc. Price Incl. | VAT % |
  // Line Total
  // Invoice / Vendor / Date appear in the separator row above each invoice group.
  // ══════════════════════════════════════════════════════════════════════
  const ws2 = wb.addWorksheet('Line Items Detail');

  // ── Row 1: Title ──────────────────────────────────────────────────────────
  _title(ws2, 10, 'VATICA AI — SALES LINE ITEMS DETAIL');

  // Column definitions — 10 cols (# + 9, mirrors Python sales_extractor.py)
  const LI_HDR = [
    '#', 'Stock Code', 'Qty', 'Description', 'Pack',
    'Unit Price Incl. VAT (E)', 'Disc. Price Excl. VAT (E)',
    'Disc. Price Incl. VAT (E)', 'VAT %',
    'Line Total (E)',
  ];
  const LI_W = [4, 13, 6, 38, 6, 16, 16, 16, 8, 14];
  const LI_MONEY_COLS = new Set([6, 7, 8, 10]);
  LI_W.forEach((w, i) => { ws2.getColumn(i + 1).width = w; });

  // ── Row 2: Subtitle note — built DIRECTLY, no spliceRows needed ───────────
  // Root cause of old "Cannot merge already merged cells" crash:
  //   _hdr() wrote individual cells at row 2  →  mergeCells('A2:M2') merged them
  //   →  spliceRows(2,0) inserted a blank row but did NOT purge 'A2:M2' from
  //   ExcelJS's internal merge registry  →  the second mergeCells('A2:M2') call
  //   immediately after found the cells still registered as merged → crash.
  // Fix: build rows in correct order (title → subtitle → headers → data).
  ws2.getRow(2).height = 16;
  ws2.mergeCells(`A2:${_col(10)}2`);
  const subCell       = ws2.getCell('A2');
  subCell.value       = 'Disc. Price columns are blank for handwritten invoices where discounted prices are not printed. Stock Code blank where absent.';
  subCell.font        = { name: 'Calibri', size: 9, color: { argb: 'FF8B0000' } };
  subCell.alignment   = { horizontal: 'left', vertical: 'center' };

  // ── Row 3: Column headers ─────────────────────────────────────────────────
  ws2.getRow(3).height = 28;
  LI_HDR.forEach((h, i) => _hdr(ws2, 3, i + 1, h, LI_W[i]));
  ws2.views = [{ state: 'frozen', ySplit: 3 }];

  let liRow = 4;
  rows.forEach((d) => {
    if (!d.line_items || !d.line_items.length) return;

    // Separator row — dark navy spanning all 10 columns
    ws2.mergeCells(`A${liRow}:${_col(10)}${liRow}`);
    ws2.getRow(liRow).height = 16;
    const sep = ws2.getCell(`A${liRow}`);
    sep.value = `   ${d.vendor_name || d.customer_name || '—'}   |   ${d.invoice_number || '—'}   |   ${d.date}   |   ${d.line_items.length} item(s)`;
    sep.font  = { bold: true, size: 9, color: { argb: WHITE }, name: 'Calibri' };
    sep.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } };
    sep.alignment = { horizontal: 'left', vertical: 'center' };
    liRow++;

    d.line_items.forEach((item) => {
      ws2.getRow(liRow).height = 17;
      const vals = [
        '',                             //  1  # (blank — matches Python sales_extractor.py)
        item.stock_code      || '',     //  2  Stock Code
        item.qty             ?? null,   //  3  Qty
        item.description     || '',     //  4  Description
        item.pack            ?? null,   //  5  Pack
        item.unit_price      ?? null,   //  6  Unit Price Incl. VAT
        item.disc_price_excl ?? null,   //  7  Disc. Price Excl. VAT
        item.disc_price_incl ?? null,   //  8  Disc. Price Incl. VAT
        item.vat_rate        ?? '',     //  9  VAT %
        item.line_total      ?? null,   // 10  Line Total
      ];
      vals.forEach((val, ci) => {
        const col  = ci + 1;
        const cell = ws2.getCell(liRow, col);
        cell.value  = val ?? null;
        cell.font   = { size: 9, name: 'Calibri' };
        cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
        cell.border = _side();
        if (LI_MONEY_COLS.has(col)) {
          cell.numFmt    = MFMT;
          cell.alignment = { horizontal: 'right',  vertical: 'center' };
        } else if (col === 4) {
          cell.alignment = { horizontal: 'left',   vertical: 'center', wrapText: true };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'center' };
        }
      });
      liRow++;
    });

    liRow++;   // blank spacer between invoices
  });

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 3 — Review (low confidence / ambiguous handwriting only)
  // ══════════════════════════════════════════════════════════════════════
  const ws3 = wb.addWorksheet('Review');
  _title(ws3, 6, 'VATICA AI — SALES REVIEW FLAGS');

  const RV_HDR   = ['Source File','Invoice No','Date','Confidence','Flag','Notes'];
  const RV_W     = [34,14,12,11,28,50];
  // Per-column horizontal alignment: Source File & Flag & Notes → left; others → center
  const RV_ALIGN = ['left','center','center','center','left','left'];
  ws3.getRow(2).height = 26;
  RV_HDR.forEach((h, i) => _hdr(ws3, 2, i + 1, h, RV_W[i]));

  let rvRow = 3;
  rows.forEach((d) => {
    const isLow = d.confidence === 'low';
    const isMed = d.confidence === 'medium';
    const isAmb = d.ambiguous;
    if (!isLow && !isMed && !isAmb) return;

    const bgHex = (isLow || isAmb) ? LOW_BG : MED_BG;
    const fgHex = (isLow || isAmb) ? LOW_FG : MED_FG;
    const flag  = isAmb
      ? '⚠ AMBIGUOUS HANDWRITING — verify amounts'
      : isLow
        ? '❌ LOW CONFIDENCE — verify before filing'
        : '⚠ MEDIUM CONFIDENCE — spot-check recommended';

    ws3.getRow(rvRow).height = 18;
    const vals = [d.file, d.invoice_number, d.date,
                  d.confidence.toUpperCase(), flag, d.notes || ''];
    vals.forEach((val, ci) => {
      const cell = ws3.getCell(rvRow, ci + 1);
      cell.value  = val;
      cell.font   = { size: 10, name: 'Calibri', color: { argb: fgHex },
                      bold: ci === 4 };
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgHex } };
      cell.border = _side();
      cell.alignment = { horizontal: RV_ALIGN[ci], vertical: 'center', wrapText: true };
    });
    rvRow++;
  });

  if (rvRow === 3) {
    ws3.mergeCells('A3:F3');
    ws3.getRow(3).height = 30;
    const good = ws3.getCell('A3');
    good.value = '✅  No review flags — all extractions are high confidence.';
    good.font  = { bold: true, size: 11, color: { argb: '1B3A2D' }, name: 'Calibri' };
    good.alignment = { horizontal: 'center', vertical: 'center' };
  }

  // ── Download (skipped when called from merge workflow) ───────────────────
  const buffer = await wb.xlsx.writeBuffer();
  if (!skipDownload) {
    const blob   = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url    = URL.createObjectURL(blob);
    const link   = document.createElement('a');
    link.href    = url;
    const safeClient = clientName ? `_${clientName.replace(/\s+/g, '_')}` : '';
    const today      = new Date().toISOString().split('T')[0];
    link.download    = `VATICA_Sales${safeClient}_${today}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }
  return buffer;
}


// ── Utility: column letter from number (1=A, 14=N …) ─────────────────────────
function _col(n) {
  let s = '';
  while (n > 0) {
    n--;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}
